import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const LS_KEY = "cart_v2"; // Updated version for store-specific carts

export function CartProvider({ children }) {
  const [storeCarts, setStoreCarts] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Snap a number to the nearest 0.25 step to avoid float artifacts
  function snapQuarter(n) {
    const x = Number(n || 0);
    return Math.round(x * 4) / 4;
  }

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(storeCarts));
    } catch {}
  }, [storeCarts]);

  // Helper function to get the first available image from a product
  function getFirstImage(product) {
    if (typeof product?.imageUrl === "string" && product.imageUrl.trim()) return product.imageUrl.trim();
    if (typeof product?.image === "string" && product.image.trim()) return product.image.trim();
    if (typeof product?.thumbnail === "string" && product.thumbnail.trim()) return product.thumbnail.trim();
    if (Array.isArray(product?.images) && product.images.length && typeof product.images[0] === "string") {
      return product.images[0].trim();
    }
    return "";
  }

  function addItem(newItem) {
    if (!newItem || !newItem.id) {
      console.error('Invalid item provided to addItem:', newItem);
      return;
    }

    // Determine store key from the item
    const storeKey = getStoreKey(newItem);
    
    setStoreCarts((prev) => {
      const currentStoreItems = prev[storeKey] || [];
      const idx = currentStoreItems.findIndex((it) => it.id === newItem.id);
      
      let updatedStoreItems;
      if (idx >= 0) {
        // Item exists, update quantity
        updatedStoreItems = [...currentStoreItems];
        const combined = Number(updatedStoreItems[idx].qty || 0) + Number(newItem.qty);
        const qty = Math.max(0.25, Math.min(999, snapQuarter(combined)));
        updatedStoreItems[idx] = { ...updatedStoreItems[idx], qty };
      } else {
        // New item
        const firstQty = Math.max(0.25, Math.min(999, snapQuarter(Number(newItem.qty))));
        const bestImage = getFirstImage(newItem);
        
        const cleanItem = {
          id: newItem.id,
          name: newItem.name || '',
          price: Number(newItem.price || 0),
          qty: firstQty,
          sku: newItem.sku || '',
          imageUrl: bestImage,
          image: bestImage,
          storeType: newItem.storeType,
          storeId: newItem.storeId,
        };
        
        updatedStoreItems = [...currentStoreItems, cleanItem];
      }
      
      return {
        ...prev,
        [storeKey]: updatedStoreItems
      };
    });
  }

  // Helper function to create a unique store key
  function getStoreKey(item) {
    if (item.storeType === 'cooperative') {
      return 'cooperative';
    } else if (item.storeType === 'member' && item.storeId) {
      return `member_${item.storeId}`;
    } else if (item.storeType === 'establishment' && item.storeId) {
      return `establishment_${item.storeId}`;
    }
    // Fallback for items without store info (legacy support)
    return 'legacy';
  }

  function setQty(storeKey, id, qty) {
    setStoreCarts((prev) => {
      const storeItems = prev[storeKey] || [];
      const updatedItems = storeItems.map((it) => (
        it.id === id
          ? { ...it, qty: Math.max(0.25, Math.min(999, snapQuarter(Number(qty)))) }
          : it
      ));
      
      return {
        ...prev,
        [storeKey]: updatedItems
      };
    });
  }

  function removeItem(storeKey, id) {
    setStoreCarts((prev) => {
      const storeItems = prev[storeKey] || [];
      const updatedItems = storeItems.filter((it) => it.id !== id);
      
      return {
        ...prev,
        [storeKey]: updatedItems
      };
    });
  }

  function clear(storeKey = null) {
    if (storeKey) {
      // Clear specific store cart
      setStoreCarts((prev) => {
        const updated = { ...prev };
        delete updated[storeKey];
        return updated;
      });
    } else {
      // Clear all carts
      setStoreCarts({});
    }
  }

  // Get items for a specific store
  function getStoreItems(storeKey) {
    return storeCarts[storeKey] || [];
  }

  // Get all store keys that have items
  function getStoreKeys() {
    return Object.keys(storeCarts).filter(key => storeCarts[key] && storeCarts[key].length > 0);
  }

  // Calculate totals for a specific store
  function getStoreTotals(storeKey) {
    const items = getStoreItems(storeKey);
    const totalQty = items.reduce((s, it) => s + (it.qty || 0), 0);
    const subtotal = items.reduce((s, it) => s + (Number(it.price || 0) * (it.qty || 0)), 0);
    return { totalQty, subtotal };
  }

  // Calculate totals across all stores
  const allStoreTotals = useMemo(() => {
    const totalQty = Object.values(storeCarts).reduce((total, items) => 
      total + items.reduce((s, it) => s + (it.qty || 0), 0), 0
    );
    const subtotal = Object.values(storeCarts).reduce((total, items) => 
      total + items.reduce((s, it) => s + (Number(it.price || 0) * (it.qty || 0)), 0), 0
    );
    return { totalQty, subtotal };
  }, [storeCarts]);

  const value = { 
    storeCarts,
    addItem, 
    setQty, 
    removeItem, 
    clear, 
    getStoreItems,
    getStoreKeys,
    getStoreTotals,
    totalQty: allStoreTotals.totalQty, 
    subtotal: allStoreTotals.subtotal 
  };
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

